import firebase from 'firebase/compat/app';
import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import 'moment/locale/uk';

// Load Ukrainian locale for moment
import i18n from './translations';

/**
 * Return the current Firestore server-side timestamp.
 * Thin wrapper to keep a single call-site across the app.
 *
 * @returns {firebase.firestore.Timestamp} Firestore Timestamp representing "now"
 */
export const updatedAtNowFirebase = () => firebase.firestore.Timestamp.now();

/**
 * Convert a Firestore timestamp-like object to a JS Date.
 *
 * Accepts objects that look like Firestore timestamps:
 * `{ seconds: number; nanoseconds: number }`.
 * Safely returns `null` if the input is falsy or malformed.
 *
 * @param {any} timestamp - Firestore timestamp or plain object with seconds/nanoseconds
 * @returns {Date | null} JS Date if conversion succeeds, otherwise null
 */
export const firebaseTimestampToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  try {
    return new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
  } catch (e) {
    console.error(
      `Error converting firebase timestamp to date: ${e} for timestamp: ${timestamp}`
    );
    return null;
  }
};

/**
 * Format a Date or date-like string to an ISO-like day precision string.
 *
 * Designed for analytics user properties (Amplitude/GA) that store dates as strings.
 * The output is UTC-normalized for consistency across time zones.
 *
 * @param {Date | string} input - Date instance or parseable date string
 * @returns {string} Formatted date string "YYYY-MM-DD" in UTC
 */
export function dateToIsoDateString(input: Date | string): string {
  return moment(input).utc().format('YYYY-MM-DD');
}

/**
 * Format a Date to a short, human-readable string.
 *
 * Example output: "Jan 5, 2025".
 * Returns `null` for falsy inputs.
 *
 * @param {Date | null} [date] - Date to format
 * @returns {string | null} Human-readable date or null
 */
export const dateToString = (date?: Date | null) => {
  if (!date) return null;
  return moment(date).format('MMM D, YYYY');
};

/**
 * Format a Date to a full localized timestamp string.
 *
 * Uses moment's "LLLL" token, which respects the active moment locale
 * (e.g., Ukrainian from the import above).
 *
 * @param {Date} date - Date to format
 * @returns {string} Localized full date-time, e.g. "Sunday, January 5, 2025 3:45 PM"
 */
export function formatDateToExactTime(date: Date): string {
  return moment(date).format('LLLL');
}

/**
 * Return a localized, human-friendly "time since" string with fallbacks.
 *
 * Rules:
 *  - < 1 minute → "Just now" (i18n key: app.dateTime.justNow)
 *  - < 60 minutes → relative minutes via moment.fromNow() (e.g., "25 minutes ago")
 *  - Same day → "Today, {time-ago}" (i18n key: app.dateTime.today)
 *  - Yesterday → "Yesterday, {HH:mm}" (i18n key: app.dateTime.yesterday)
 *  - Older → localized short datetime (moment "lll")
 *
 * Locale comes from moment (see uk import) and text from your i18n keys.
 *
 * @param {Date} date - Past date to compare with "now"
 * @returns {string} Human-friendly relative time string
 */
export function formatCustomDistanceToNow(date: Date): string {
  const now = moment();
  const momentDate = moment(date);
  const diffMinutes = now.diff(momentDate, 'minutes');
  const isToday = momentDate.isSame(now, 'day');
  const isYesterday = momentDate.isSame(now.clone().subtract(1, 'day'), 'day');

  if (diffMinutes < 1) {
    return i18n.t('app.dateTime.justNow');
  }

  if (diffMinutes < 60) {
    return momentDate.fromNow();
  }

  if (isToday) {
    const hoursAgo = momentDate.fromNow();
    return i18n.t('app.dateTime.today', { time: hoursAgo });
  }

  if (isYesterday) {
    return i18n.t('app.dateTime.yesterday', { time: momentDate.format('LT') });
  }

  return momentDate.format('lll');
}
